import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReportDesignerModal from './ReportDesignerModal';
import type { ReportDefinition, Theme } from './types';

function getPortalTheme(): Theme {
  const activeTheme = document.documentElement.getAttribute('data-theme');
  return activeTheme === 'white' ? 'light' : 'dark';
}

const ANALYTICS_HUB_PATH = '/portal/analytics-hub';

const ReportDesignerPage: React.FC = () => {
  const navigate = useNavigate();
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const [theme, setTheme] = useState<Theme>(getPortalTheme());

  const collectionId = searchParams.get('collection') ?? '';
  const isNewReport = !reportId || reportId === 'new';

  useEffect(() => {
    setTheme(getPortalTheme());
  }, []);

  const report = useMemo<ReportDefinition | null>(() => {
    if (isNewReport) return null;
    return {
      id: reportId!,
      name: '',
      description: '',
      category: 'Custom',
      icon: 'barChart',
      collectionId,
      status: 'draft',
    };
  }, [collectionId, isNewReport, reportId]);

  const handleClose = () => {
    const params = collectionId ? `?collection=${encodeURIComponent(collectionId)}` : '';
    navigate(`${ANALYTICS_HUB_PATH}${params}`);
  };

  if (isNewReport && !collectionId) {
    return (
      <div className="h-full min-h-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-sm text-neutral-400">Collection is required to create a new report.</p>
        <button
          type="button"
          onClick={() => navigate(ANALYTICS_HUB_PATH)}
          className="px-3 py-1.5 rounded-md text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
        >
          Back to Analytics Hub
        </button>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col">
      <ReportDesignerModal
        theme={theme}
        report={report}
        collectionId={collectionId}
        onClose={handleClose}
      />
    </div>
  );
};

export default ReportDesignerPage;
